import React, { useState, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import { Spin, Input, Button, Select, message } from "antd";
import {
  ArrowLeftOutlined,
  SaveOutlined,
  SendOutlined,
  PlusOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import LayoutWrapper from "@iso/components/utility/layoutWrapper.js";
import Box from "@iso/components/utility/box";
import { incidentService } from "@iso/services";
import useIsSafetyAdmin from "./shared/useIsSafetyAdmin";
import useInvestigationAccess from "./shared/useInvestigationAccess";
import useInvestigationHandlers from "./shared/useInvestigationHandlers";
import InvestigationStepper from "./shared/InvestigationStepper";
import CorrectiveActionStatus from "./shared/CorrectiveActionStatus";
import useCorrectiveActionContext, {
  todayISO,
  validateCorrectiveActions,
  validateCorrectiveActionDescriptions,
} from "./shared/correctiveActionHelpers";
import { InvestigationFormWrapper } from "./InvestigationForm.styles";

const { TextArea } = Input;
const { Option } = Select;

const STEPS = [
  { number: 1, title: "Incident Overview", description: "Initial details" },
  { number: 2, title: "Timeline", description: "Sequence of events" },
  { number: 3, title: "Contributing Factors", description: "Analyze factors" },
  { number: 4, title: "Root Causes", description: "Identify causes" },
  { number: 5, title: "Actions & Learning", description: "Plan & document" },
];

const FACTOR_CATEGORIES = [
  {
    key: "taskFactors",
    title: "Task Factors",
    description: "Factors related to the task design, procedures, and task demands",
    colorClass: "factor-section-indigo",
    placeholder: "e.g., Unclear procedures, inadequate task planning...",
    addLabel: "Add Task Factor",
  },
  {
    key: "individualFactors",
    title: "Individual Factors",
    description: "Factors related to individual knowledge, skills, and human factors",
    colorClass: "factor-section-purple",
    placeholder: "e.g., Lack of training, fatigue, inexperience...",
    addLabel: "Add Individual Factor",
  },
  {
    key: "teamFactors",
    title: "Team Factors",
    description: "Factors related to team communication, supervision, and collaboration",
    colorClass: "factor-section-blue",
    placeholder: "e.g., Poor communication, lack of supervision...",
    addLabel: "Add Team Factor",
  },
  {
    key: "workplaceFactors",
    title: "Workplace Factors",
    description: "Factors related to equipment, environment, and workplace conditions",
    colorClass: "factor-section-green",
    placeholder: "e.g., Equipment malfunction, poor lighting, hazardous conditions...",
    addLabel: "Add Workplace Factor",
  },
  {
    key: "organizationalFactors",
    title: "Organizational Factors",
    description: "Factors related to organizational systems, policies, and culture",
    colorClass: "factor-section-orange",
    placeholder: "e.g., Inadequate policies, poor safety culture, resource constraints...",
    addLabel: "Add Organizational Factor",
  },
];

const ROOT_CAUSE_CATEGORIES = ["Task", "Individual", "Team", "Workplace", "Organizational"];

const DEFAULT_FORM = {
  incidentOverview: "",
  immediateActions: "",
  events: [{ time: "", description: "", evidence: "" }],
  taskFactors: [""],
  individualFactors: [""],
  teamFactors: [""],
  workplaceFactors: [""],
  organizationalFactors: [""],
  rootCauses: [{ category: "", description: "" }],
  correctiveActions: [{ id: undefined, action: "", assignedTo: undefined, dueDate: "", priority: "Medium" }],
  lessonsLearned: "",
};

// Pre-Task-1 drafts stored CAs with a free-text `responsibility` field; new
// shape uses `assignedTo` (user id). Coerce so old drafts render cleanly with
// the dropdown empty (forcing the investigator to pick a real assignee).
const normalizeCorrectiveAction = (ca) => ({
  id: ca?.id,
  action: ca?.action || "",
  assignedTo: ca?.assignedTo,
  dueDate: ca?.dueDate || "",
  priority: ca?.priority || "Medium",
});

const parseContent = (content, incidentDescription) => {
  if (!content) {
    return { ...DEFAULT_FORM, incidentOverview: incidentDescription || "" };
  }
  try {
    const parsed = JSON.parse(content);
    if (parsed && Array.isArray(parsed.events)) {
      const merged = { ...DEFAULT_FORM, ...parsed };
      if (Array.isArray(merged.correctiveActions)) {
        merged.correctiveActions = merged.correctiveActions.map(normalizeCorrectiveAction);
      }
      return merged;
    }
    return { ...DEFAULT_FORM, incidentOverview: content };
  } catch {
    return { ...DEFAULT_FORM, incidentOverview: content };
  }
};

const ICAMInvestigation = () => {
  const { incidentId } = useParams();
  const history = useHistory();
  const [incident, setIncident] = useState(null);
  const [investigation, setInvestigation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState(DEFAULT_FORM);
  const [reviewComment, setReviewComment] = useState("");

  const isSafetyAdmin = useIsSafetyAdmin();
  const { staff, isCALocked, getLiveCA } = useCorrectiveActionContext(
    incidentId,
    incident?.property_id
  );

  useEffect(() => {
    if (incidentId) loadData();
  }, [incidentId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [incidentData, investigationData] = await Promise.all([
        incidentService.getById(incidentId),
        incidentService.getInvestigation(incidentId),
      ]);
      setIncident(incidentData);
      setInvestigation(investigationData);
      if (investigationData) {
        setFormData(parseContent(investigationData.content, incidentData?.description));
        setReviewComment(investigationData.review_comment || "");
      } else if (incidentData) {
        setFormData({ ...DEFAULT_FORM, incidentOverview: incidentData.description || "" });
      }
    } catch (err) {
      console.error("Failed to load ICAM investigation data:", err);
      message.error("Failed to load investigation data.");
    } finally {
      setLoading(false);
    }
  };

  const {
    canEdit, canSubmit, isViewOnly, isApproved, isSubmitted,
  } = useInvestigationAccess(investigation, isSafetyAdmin);

  const {
    saving, submitting, approving,
    handleSaveDraft, handleSubmit, handleApprove, handleRequestMoreInfo,
  } = useInvestigationHandlers(incidentId, formData, investigation, reviewComment);

  // --- Event helpers ---
  const addEvent = () => {
    setFormData({ ...formData, events: [...formData.events, { time: "", description: "", evidence: "" }] });
  };
  const removeEvent = (index) => {
    setFormData({ ...formData, events: formData.events.filter((_, i) => i !== index) });
  };
  const updateEvent = (index, field, value) => {
    const events = [...formData.events];
    events[index] = { ...events[index], [field]: value };
    setFormData({ ...formData, events });
  };

  // --- Factor helpers ---
  const addFactor = (key) => {
    setFormData({ ...formData, [key]: [...formData[key], ""] });
  };
  const removeFactor = (key, index) => {
    setFormData({ ...formData, [key]: formData[key].filter((_, i) => i !== index) });
  };
  const updateFactor = (key, index, value) => {
    const updated = [...formData[key]];
    updated[index] = value;
    setFormData({ ...formData, [key]: updated });
  };

  // --- Root cause helpers ---
  const addRootCause = () => {
    setFormData({ ...formData, rootCauses: [...formData.rootCauses, { category: "", description: "" }] });
  };
  const removeRootCause = (index) => {
    setFormData({ ...formData, rootCauses: formData.rootCauses.filter((_, i) => i !== index) });
  };
  const updateRootCause = (index, field, value) => {
    const rootCauses = [...formData.rootCauses];
    rootCauses[index] = { ...rootCauses[index], [field]: value };
    setFormData({ ...formData, rootCauses });
  };

  // --- Corrective action helpers ---
  const addCorrectiveAction = () => {
    setFormData({
      ...formData,
      correctiveActions: [
        ...formData.correctiveActions,
        { id: undefined, action: "", assignedTo: undefined, dueDate: "", priority: "Medium" },
      ],
    });
  };
  const removeCorrectiveAction = (index) => {
    const target = formData.correctiveActions[index];
    if (isCALocked(target)) {
      message.warning("Cannot remove a corrective action that has already started.");
      return;
    }
    setFormData({ ...formData, correctiveActions: formData.correctiveActions.filter((_, i) => i !== index) });
  };
  const updateCorrectiveAction = (index, field, value) => {
    const actions = [...formData.correctiveActions];
    actions[index] = { ...actions[index], [field]: value };
    setFormData({ ...formData, correctiveActions: actions });
  };

  const onSubmitClick = () => {
    const error = validateCorrectiveActionDescriptions(formData.correctiveActions);
    if (error) {
      message.error(error);
      setCurrentStep(5);
      return;
    }
    handleSubmit();
  };

  const onApproveClick = () => {
    const error = validateCorrectiveActions(formData.correctiveActions);
    if (error) {
      setCurrentStep(5);
      message.error(error);
      return;
    }
    handleApprove(formData.correctiveActions);
  };

  // Approver sets assignee + due date at the approval step (Mary 2026-05-06).
  const showApprovalFields = isSafetyAdmin || isSubmitted || isApproved;
  const approvalFieldsLocked = !isSafetyAdmin || isApproved;

  if (loading) {
    return (
      <LayoutWrapper><Box><InvestigationFormWrapper>
        <div className="loading-container"><Spin size="large" /></div>
      </InvestigationFormWrapper></Box></LayoutWrapper>
    );
  }

  if (!incident) {
    return (
      <LayoutWrapper><Box><InvestigationFormWrapper>
        <div className="not-found-container">
          <h2 className="not-found-title">Incident not found</h2>
          <span className="not-found-link" onClick={() => history.push("/incidents")}>Return to incidents</span>
        </div>
      </InvestigationFormWrapper></Box></LayoutWrapper>
    );
  }

  return (
    <LayoutWrapper>
      <Box>
        <InvestigationFormWrapper>
          {/* Header */}
          <div className="inv-header">
            <div>
              <button className="inv-back-button" onClick={() => history.push("/incidents")}>
                <ArrowLeftOutlined /> Back to Incidents
              </button>
              <h1 className="inv-title">ICAM Investigation</h1>
              <p className="inv-subtitle">
                {incident.report_number} — {incident.description || ""}
              </p>
            </div>
            <div className="inv-header-actions">
              {canEdit && (
                <Button className="btn-save-draft" onClick={handleSaveDraft} loading={saving}>
                  <SaveOutlined /> Save Draft
                </Button>
              )}
              {canSubmit && (
                <Button type="primary" className="btn-submit" onClick={onSubmitClick} loading={submitting}>
                  <SendOutlined /> Submit Investigation
                </Button>
              )}
            </div>
          </div>

          {/* Approved Notice */}
          {isApproved && (
            <div className="readonly-notice">
              <CheckCircleOutlined style={{ marginRight: 8 }} />
              This investigation has been approved and is read-only.
            </div>
          )}

          {/* View-Only Notice */}
          {isViewOnly && !isApproved && (
            <div className="readonly-notice" style={{ background: "#eff6ff", borderColor: "#bfdbfe", color: "#1e40af" }}>
              You are viewing this investigation in read-only mode. Only the assigned investigator and Safety Admins can make changes.
            </div>
          )}

          {/* Safety Admin Review */}
          {isSafetyAdmin && isSubmitted && (
            <div className="review-section">
              <h3 className="review-title">Safety Admin Review</h3>
              <p className="review-status">This investigation has been submitted and is awaiting your review.</p>
              <TextArea
                rows={3}
                placeholder="Add a review comment (optional)"
                value={reviewComment}
                onChange={(e) => setReviewComment(e.target.value)}
                style={{ marginBottom: 12 }}
              />
              <div className="review-actions">
                <Button
                  className="btn-approve"
                  type="primary"
                  onClick={onApproveClick}
                  loading={approving}
                >
                  Approve Investigation
                </Button>
                <Button className="btn-request-info" onClick={handleRequestMoreInfo} loading={approving}>Request More Info</Button>
              </div>
            </div>
          )}

          <InvestigationStepper steps={STEPS} currentStep={currentStep} onStepClick={setCurrentStep} />

          {/* Form Content */}
          <div className="form-card">
            {/* Step 1: Incident Overview */}
            {currentStep === 1 && (
              <div>
                <h2 className="step-title">Step 1: Incident Overview</h2>
                <p className="step-description">
                  Provide a comprehensive overview of what happened and what immediate actions were taken.
                </p>
                <label className="field-label">Incident Description</label>
                <TextArea
                  rows={5}
                  value={formData.incidentOverview}
                  onChange={(e) => setFormData({ ...formData, incidentOverview: e.target.value })}
                  placeholder="Describe what happened, where, when, who was involved, and the immediate impact..."
                  disabled={!canEdit}
                  style={{ marginBottom: 20 }}
                />
                <label className="field-label">Immediate Actions Taken</label>
                <TextArea
                  rows={5}
                  value={formData.immediateActions}
                  onChange={(e) => setFormData({ ...formData, immediateActions: e.target.value })}
                  placeholder="What actions were taken immediately after the incident? (e.g., first aid, scene secured, notifications made)..."
                  disabled={!canEdit}
                />
                <div className="tips-box tips-box-blue">
                  <p className="tips-box-title">ICAM Framework Tips:</p>
                  <ul className="tips-box-list">
                    <li>Focus on facts, not opinions or blame</li>
                    <li>Include environmental conditions and context</li>
                    <li>Document both direct and indirect impacts</li>
                    <li>Note any deviations from normal procedures</li>
                  </ul>
                </div>
                <div className="step-nav" style={{ justifyContent: "flex-end" }}>
                  <Button type="primary" className="btn-submit" onClick={() => setCurrentStep(2)}>
                    Continue to Timeline
                  </Button>
                </div>
              </div>
            )}

            {/* Step 2: Timeline */}
            {currentStep === 2 && (
              <div>
                <h2 className="step-title">Step 2: Timeline — Sequence of Events</h2>
                <p className="step-description">
                  Document the chronological sequence of events leading up to, during, and after the incident.
                </p>

                {formData.events.map((event, index) => (
                  <div className="event-card" key={index}>
                    <div className="event-card-header">
                      <span className="event-title">Event {index + 1}</span>
                      {canEdit && formData.events.length > 1 && (
                        <button className="btn-delete-icon" onClick={() => removeEvent(index)}>
                          <DeleteOutlined />
                        </button>
                      )}
                    </div>
                    <div className="event-grid">
                      <div>
                        <label className="field-label-sm">Time</label>
                        <Input
                          value={event.time}
                          onChange={(e) => updateEvent(index, "time", e.target.value)}
                          placeholder="e.g., 08:30 AM"
                          disabled={!canEdit}
                        />
                      </div>
                      <div>
                        <label className="field-label-sm">Description</label>
                        <TextArea
                          rows={3}
                          value={event.description}
                          onChange={(e) => updateEvent(index, "description", e.target.value)}
                          placeholder="What happened at this time?"
                          disabled={!canEdit}
                        />
                      </div>
                    </div>
                    <div style={{ marginTop: 12 }}>
                      <label className="field-label-sm">Evidence/Source</label>
                      <Input
                        value={event.evidence}
                        onChange={(e) => updateEvent(index, "evidence", e.target.value)}
                        placeholder="e.g., Witness statement, CCTV footage, physical evidence"
                        disabled={!canEdit}
                      />
                    </div>
                  </div>
                ))}

                {canEdit && (
                  <Button icon={<PlusOutlined />} onClick={addEvent} style={{ marginBottom: 24 }}>
                    Add Event
                  </Button>
                )}

                <div className="step-nav">
                  <Button onClick={() => setCurrentStep(1)}>Back</Button>
                  <Button type="primary" className="btn-submit" onClick={() => setCurrentStep(3)}>
                    Continue to Contributing Factors
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Contributing Factors */}
            {currentStep === 3 && (
              <div>
                <h2 className="step-title">Step 3: Contributing Factors Analysis</h2>
                <p className="step-description">
                  Identify factors across different categories that contributed to the incident.
                </p>

                {FACTOR_CATEGORIES.map((cat) => (
                  <div className={`factor-section ${cat.colorClass}`} key={cat.key}>
                    <h3 className="factor-title">{cat.title}</h3>
                    <p className="factor-desc">{cat.description}</p>
                    {(formData[cat.key] || [""]).map((factor, index) => (
                      <div className="factor-input-row" key={index}>
                        <Input
                          style={{ flex: 1, background: "#fff" }}
                          value={factor}
                          onChange={(e) => updateFactor(cat.key, index, e.target.value)}
                          placeholder={cat.placeholder}
                          disabled={!canEdit}
                        />
                        {canEdit && formData[cat.key].length > 1 && (
                          <button className="btn-delete-icon" onClick={() => removeFactor(cat.key, index)}>
                            <DeleteOutlined />
                          </button>
                        )}
                      </div>
                    ))}
                    {canEdit && (
                      <Button size="small" icon={<PlusOutlined />} onClick={() => addFactor(cat.key)} style={{ marginTop: 8 }}>
                        {cat.addLabel}
                      </Button>
                    )}
                  </div>
                ))}

                <div className="step-nav">
                  <Button onClick={() => setCurrentStep(2)}>Back</Button>
                  <Button type="primary" className="btn-submit" onClick={() => setCurrentStep(4)}>
                    Continue to Root Causes
                  </Button>
                </div>
              </div>
            )}

            {/* Step 4: Root Causes */}
            {currentStep === 4 && (
              <div>
                <h2 className="step-title">Step 4: Root Cause Identification</h2>
                <p className="step-description">
                  Based on the contributing factors, identify the underlying root causes of the incident.
                </p>

                {formData.rootCauses.map((rc, index) => (
                  <div className="root-cause-card" key={index}>
                    <div className="root-cause-header">
                      <span className="event-title">Root Cause {index + 1}</span>
                      {canEdit && formData.rootCauses.length > 1 && (
                        <button className="btn-delete-icon" onClick={() => removeRootCause(index)}>
                          <DeleteOutlined />
                        </button>
                      )}
                    </div>
                    <label className="field-label-sm">Category</label>
                    <Select
                      style={{ width: "100%", marginBottom: 12 }}
                      value={rc.category || undefined}
                      onChange={(val) => updateRootCause(index, "category", val)}
                      placeholder="Select a category"
                      disabled={!canEdit}
                    >
                      {ROOT_CAUSE_CATEGORIES.map((cat) => (
                        <Option key={cat} value={cat}>{cat}</Option>
                      ))}
                    </Select>
                    <label className="field-label-sm">Root Cause Description</label>
                    <TextArea
                      rows={4}
                      value={rc.description}
                      onChange={(e) => updateRootCause(index, "description", e.target.value)}
                      placeholder="Describe the root cause in detail..."
                      disabled={!canEdit}
                    />
                  </div>
                ))}

                {canEdit && (
                  <Button icon={<PlusOutlined />} onClick={addRootCause} style={{ marginBottom: 24 }}>
                    Add Root Cause
                  </Button>
                )}

                <div className="step-nav">
                  <Button onClick={() => setCurrentStep(3)}>Back</Button>
                  <Button type="primary" className="btn-submit" onClick={() => setCurrentStep(5)}>
                    Continue to Actions & Learning
                  </Button>
                </div>
              </div>
            )}

            {/* Step 5: Corrective Actions & Lessons Learned */}
            {currentStep === 5 && (
              <div>
                <h2 className="step-title">Step 5: Corrective Actions & Lessons Learned</h2>
                <p className="step-description">
                  Define corrective actions to address root causes and document lessons learned.
                </p>

                <h3 style={{ fontSize: 18, fontWeight: 600, color: "#101828", marginBottom: 16 }}>
                  Corrective Actions
                </h3>

                {formData.correctiveActions.map((storedAction, index) => {
                  // Render live values for actions that already exist in the
                  // Corrective Actions module — completion, reallocation and a
                  // revised due date all land there, not in this JSON snapshot.
                  const action = getLiveCA(storedAction);
                  const locked = isCALocked(action);
                  const fieldDisabled = !canEdit || locked;
                  return (
                  <div className="action-card" key={index}>
                    <div className="action-card-header">
                      <span style={{ fontSize: 14, fontWeight: 600, color: "#101828" }}>Action {index + 1}</span>
                      {canEdit && formData.correctiveActions.length > 1 && !locked && (
                        <button className="btn-delete-icon" onClick={() => removeCorrectiveAction(index)}>
                          <DeleteOutlined />
                        </button>
                      )}
                    </div>
                    <CorrectiveActionStatus action={action} />
                    <label className="field-label-sm">Action Description</label>
                    <TextArea
                      rows={3}
                      value={action.action}
                      onChange={(e) => updateCorrectiveAction(index, "action", e.target.value)}
                      placeholder="Describe the corrective action in detail..."
                      disabled={fieldDisabled}
                      style={{ marginBottom: 16 }}
                    />
                    {showApprovalFields && (
                      <div className="action-grid">
                        <div>
                          <label className="field-label-sm">Assignee</label>
                          <Select
                            style={{ width: "100%" }}
                            showSearch
                            optionFilterProp="children"
                            filterOption={(input, option) =>
                              (option?.children || "")
                                .toString()
                                .toLowerCase()
                                .includes(input.toLowerCase())
                            }
                            value={action.assignedTo || undefined}
                            onChange={(val) => updateCorrectiveAction(index, "assignedTo", val)}
                            placeholder="Select staff member"
                            disabled={approvalFieldsLocked || locked}
                          >
                            {staff.map((u) => (
                              <Option key={u.id} value={u.id}>{u.name || u.full_name || u.email}</Option>
                            ))}
                          </Select>
                        </div>
                        <div>
                          <label className="field-label-sm">Due Date</label>
                          <Input
                            type="date"
                            min={todayISO()}
                            value={action.dueDate}
                            status={action.dueDate && action.dueDate < todayISO() ? "error" : ""}
                            onChange={(e) => updateCorrectiveAction(index, "dueDate", e.target.value)}
                            disabled={approvalFieldsLocked || locked}
                          />
                          {action.dueDate && action.dueDate < todayISO() && (
                            <span style={{ color: "#ff4d4f", fontSize: 12 }}>Due date cannot be in the past.</span>
                          )}
                        </div>
                      </div>
                    )}
                    {!showApprovalFields && (
                      <p className="step-description" style={{ marginTop: 0, fontSize: 12, color: "#667085" }}>
                        The Safety Admin will assign this action and set the due date at the approval step.
                      </p>
                    )}
                    <div style={{ marginTop: 16 }}>
                      <label className="field-label-sm">Priority</label>
                      <div className="priority-group">
                        {["High", "Medium", "Low"].map((p) => (
                          <label className={`priority-label priority-${p.toLowerCase()}`} key={p}>
                            <input
                              type="radio"
                              name={`priority-${index}`}
                              checked={action.priority === p}
                              onChange={() => updateCorrectiveAction(index, "priority", p)}
                              disabled={fieldDisabled}
                              style={{ accentColor: "#0B2A1B" }}
                            />
                            {p}
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                  );
                })}

                {canEdit && (
                  <Button icon={<PlusOutlined />} onClick={addCorrectiveAction} style={{ marginBottom: 24 }}>
                    Add Corrective Action
                  </Button>
                )}

                <h3 style={{ fontSize: 18, fontWeight: 600, color: "#101828", marginBottom: 12 }}>
                  Lessons Learned
                </h3>
                <p className="step-description" style={{ marginBottom: 12 }}>
                  Document key learnings that can be shared across the organization to prevent similar incidents.
                </p>
                {isApproved ? (
                  <div>
                    <div
                      style={{
                        background: "#f9fafb",
                        border: "1px solid #e5e7eb",
                        borderRadius: 8,
                        padding: 16,
                        fontSize: 14,
                        lineHeight: 1.6,
                        color: "#101828",
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      {formData.lessonsLearned || "No lessons learned were recorded during the investigation."}
                    </div>
                    <p className="step-description" style={{ marginTop: 12, marginBottom: 0, fontSize: 12 }}>
                      This investigation is approved, so its lessons learned are now fixed.{" "}
                      <span
                        className="not-found-link"
                        onClick={() => history.push(`/incidents/${incidentId}/lessons-learned`)}
                      >
                        Edit and distribute them in the Lessons Learned module
                      </span>
                      .
                    </p>
                  </div>
                ) : (
                  <TextArea
                    rows={6}
                    value={formData.lessonsLearned}
                    onChange={(e) => setFormData({ ...formData, lessonsLearned: e.target.value })}
                    placeholder="What can we learn from this incident? What should others in the organization know?"
                    disabled={!canEdit}
                  />
                )}

                <div className="step-nav" style={{ marginTop: 32 }}>
                  <Button onClick={() => setCurrentStep(4)}>Back</Button>
                  <div className="step-nav-end">
                    {canEdit && (
                      <Button className="btn-save-draft" onClick={handleSaveDraft} loading={saving}>
                        <SaveOutlined /> Save Draft
                      </Button>
                    )}
                    {canSubmit && (
                      <Button type="primary" className="btn-submit" onClick={onSubmitClick} loading={submitting}>
                        <SendOutlined /> Submit Investigation
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </InvestigationFormWrapper>
      </Box>
    </LayoutWrapper>
  );
};

export default ICAMInvestigation;
